import React, { useState, useEffect, useRef } from 'react';
import {
  promotionService,
  extractYouTubeId,
  getYouTubeThumbnail,
  fetchVimeoThumbnail,
  uploadMediaFile
} from '../../services/promotionService';
import { useAuth } from '../../controllers/auth';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  Film,
  Plus,
  Trash2,
  Edit2,
  Play,
  Eye,
  X,
  Search,
  Upload,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
  Calendar,
  Layers,
  Smartphone,
  Globe,
  MoveUp,
  MoveDown,
  AlertCircle,
  Loader2
} from 'lucide-react';

const PromotionVideos = () => {
  const { user, logAction } = useAuth();
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [platformFilter, setPlatformFilter] = useState('ALL');
  const [positionFilter, setPositionFilter] = useState('ALL');

  // Modal State for Add / Edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [sourceType, setSourceType] = useState('youtube'); // 'youtube' | 'upload'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    show_on: 'Both',
    position: 'Promotion Section',
    priority: 1,
    status: 'Active',
    start_date: '',
    end_date: ''
  });

  // Upload Progress & Local Files
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Video Live Preview Modal
  const [previewVideo, setPreviewVideo] = useState(null);

  // Confirmation Modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const fileInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  // Load Promotion Videos
  const loadPromotions = async () => {
    setLoading(true);
    const { data } = await promotionService.getPromotions();
    setPromotions(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadPromotions();
  }, []);

  // Open Modal for Create or Edit
  const handleOpenModal = (promo = null) => {
    setFormError('');
    setUploadProgress(0);
    setVideoFile(null);
    setThumbnailFile(null);

    if (promo) {
      setEditingPromo(promo);
      setSourceType(promo.source_type || 'youtube');
      setFormData({
        title: promo.title || '',
        description: promo.description || '',
        video_url: promo.video_url || '',
        thumbnail_url: promo.thumbnail_url || '',
        show_on: promo.show_on || 'Both',
        position: promo.position || 'Promotion Section',
        priority: promo.priority || 1,
        status: promo.status || 'Active',
        start_date: promo.start_date ? promo.start_date.slice(0, 16) : '',
        end_date: promo.end_date ? promo.end_date.slice(0, 16) : ''
      });
    } else {
      setEditingPromo(null);
      setSourceType('youtube');
      setFormData({
        title: '',
        description: '',
        video_url: '',
        thumbnail_url: '',
        show_on: 'Both',
        position: 'Promotion Section',
        priority: promotions.length + 1,
        status: 'Active',
        start_date: '',
        end_date: ''
      });
    }
    setIsModalOpen(true);
  };

  // Video URL Change Handler (Auto Thumbnail Fetch)
  const handleVideoUrlChange = async (url) => {
    setFormData((prev) => ({ ...prev, video_url: url }));
    if (!url) return;

    // Check if YouTube
    const ytId = extractYouTubeId(url);
    if (ytId) {
      const ytThumb = getYouTubeThumbnail(ytId);
      setFormData((prev) => ({ ...prev, thumbnail_url: ytThumb }));
      return;
    }

    // Check if Vimeo
    if (url.includes('vimeo.com')) {
      const vimeoThumb = await fetchVimeoThumbnail(url);
      if (vimeoThumb) {
        setFormData((prev) => ({ ...prev, thumbnail_url: vimeoThumb }));
      }
    }
  };

  // Video File Selection Handler
  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['video/mp4', 'video/webm'].includes(file.type)) {
      setFormError('Only MP4 and WebM video formats are allowed.');
      return;
    }

    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > 60) {
      setFormError(`Video file is ${Math.round(sizeMb)}MB. Max allowed limit is 60MB.`);
      return;
    }

    setFormError('');
    setVideoFile(file);
    setFormData((prev) => ({
      ...prev,
      title: prev.title || file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
    }));
  };

  // Thumbnail File Selection Handler
  const handleThumbnailFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setFormError('Only JPG, PNG, and WebP thumbnail images are allowed.');
      return;
    }

    setFormError('');
    setThumbnailFile(file);
    // Temporary preview
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, thumbnail_url: previewUrl }));
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.title.trim()) {
      setFormError('Please enter a video title.');
      return;
    }

    if (sourceType === 'youtube' && !formData.video_url.trim()) {
      setFormError('Please provide a valid YouTube or Vimeo video link.');
      return;
    }

    if (sourceType === 'upload' && !videoFile && !formData.video_url) {
      setFormError('Please select an MP4/WebM video file to upload.');
      return;
    }

    setIsSubmitting(true);
    try {
      let finalVideoUrl = formData.video_url;
      let finalThumbnailUrl = formData.thumbnail_url;

      // 1. Upload Video File if selected
      if (sourceType === 'upload' && videoFile) {
        finalVideoUrl = await uploadMediaFile(videoFile, 'promo_video', (pct) => {
          setUploadProgress(pct);
        });
      }

      // 2. Upload Custom Thumbnail File if selected
      if (thumbnailFile) {
        finalThumbnailUrl = await uploadMediaFile(thumbnailFile, 'promo_thumb', null);
      }

      const payload = {
        ...formData,
        source_type: sourceType,
        video_url: finalVideoUrl,
        thumbnail_url: finalThumbnailUrl,
        created_by: user?.id || null
      };

      if (editingPromo) {
        await promotionService.updatePromotion(editingPromo.id, payload);
        await logAction(user?.id, user?.full_name || 'Admin', 'UPDATE_PROMOTION_VIDEO', editingPromo.id, {
          title: payload.title
        });
      } else {
        await promotionService.createPromotion(payload);
        await logAction(user?.id, user?.full_name || 'Admin', 'CREATE_PROMOTION_VIDEO', 'new', {
          title: payload.title
        });
      }

      setIsModalOpen(false);
      await loadPromotions();
    } catch (err) {
      console.error('Save failed:', err);
      setFormError(err.message || 'Failed to save promotion video.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Instant Status Toggle
  const handleToggleStatus = async (promo) => {
    const updated = await promotionService.toggleStatus(promo.id, promo.status);
    if (updated.data) {
      setPromotions((prev) =>
        prev.map((p) => (p.id === promo.id ? { ...p, status: p.status === 'Active' ? 'Inactive' : 'Active' } : p))
      );
    }
  };

  // Delete Handler
  const handleDeleteClick = (promo) => {
    setConfirmTitle('Delete Promotion Video');
    setConfirmMessage(`Are you sure you want to permanently delete "${promo.title}"? This video will immediately disappear from both the Website and App.`);
    setPendingAction(() => async () => {
      await promotionService.deletePromotion(promo.id, promo.video_url);
      await logAction(user?.id, user?.full_name || 'Admin', 'DELETE_PROMOTION_VIDEO', promo.id, {
        title: promo.title
      });
      await loadPromotions();
    });
    setConfirmOpen(true);
  };

  // Move Priority Up / Down
  const handleMoveOrder = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= filteredPromotions.length) return;

    const list = [...filteredPromotions];
    const [moved] = list.splice(index, 1);
    list.splice(newIndex, 0, moved);

    setPromotions(list);
    await promotionService.updatePriorities(list);
  };

  // Filtered List
  const filteredPromotions = promotions.filter((promo) => {
    const matchesSearch =
      (promo.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (promo.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || promo.status === statusFilter;
    const matchesPlatform = platformFilter === 'ALL' || promo.show_on === platformFilter || promo.show_on === 'Both';
    const matchesPosition = positionFilter === 'ALL' || promo.position === positionFilter;
    return matchesSearch && matchesStatus && matchesPlatform && matchesPosition;
  });

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '28px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              backgroundColor: 'rgba(168, 8, 12, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--primary)'
            }}>
              <Film size={22} />
            </div>
            <div>
              <h1 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '-0.02em',
                margin: 0
              }}>
                Brand Promotion Videos
              </h1>
              <p style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                margin: '2px 0 0'
              }}>
                Publish & manage brand promotional videos featured across the Website and Mobile App.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
          style={{
            height: '44px',
            padding: '0 20px',
            borderRadius: '10px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(168, 8, 12, 0.28)'
          }}
        >
          <Plus size={18} />
          <span>Add New Video</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '14px',
        border: '1px solid var(--border-primary)',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
      }}>
        <div style={{ position: 'relative', minWidth: '280px', flex: '1 1 300px' }}>
          <Search size={17} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              paddingLeft: '38px',
              paddingRight: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              height: '40px',
              padding: '0 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">All Status</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive</option>
          </select>

          {/* Platform Filter */}
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            style={{
              height: '40px',
              padding: '0 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">All Platforms</option>
            <option value="Both">Both (Web & App)</option>
            <option value="Website">Website Only</option>
            <option value="App">Mobile App Only</option>
          </select>

          {/* Position Filter */}
          <select
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            style={{
              height: '40px',
              padding: '0 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">All Positions</option>
            <option value="Promotion Section">Promotion Section</option>
            <option value="Homepage Hero">Homepage Hero</option>
            <option value="Reels Page">Reels Page</option>
          </select>
        </div>
      </div>

      {/* Videos List / Table */}
      {loading ? (
        <div style={{
          padding: '60px',
          textAlign: 'center',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '16px',
          border: '1px solid var(--border-primary)'
        }}>
          <Loader2 size={32} className="spin" style={{ margin: '0 auto 12px', color: 'var(--primary)' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading promotion videos...</p>
        </div>
      ) : filteredPromotions.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '16px',
          border: '1px dashed var(--border-primary)'
        }}>
          <Film size={44} style={{ color: 'var(--primary)', opacity: 0.5, margin: '0 auto 12px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
            No Promotion Videos Found
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '420px', margin: '0 auto 20px' }}>
            {searchTerm || statusFilter !== 'ALL'
              ? 'No videos match your active filter criteria. Try resetting filters.'
              : 'Add your first high-converting brand promotion video to showcase on the website and app.'}
          </p>
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-primary"
            style={{ height: '40px', padding: '0 18px', borderRadius: '8px' }}
          >
            <Plus size={16} /> Add First Video
          </button>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '16px',
          border: '1px solid var(--border-primary)',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{
                  borderBottom: '1px solid var(--border-primary)',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: 'var(--text-secondary)'
                }}>
                  <th style={{ padding: '14px 16px', width: '70px', textAlign: 'center' }}>Order</th>
                  <th style={{ padding: '14px 16px', width: '130px' }}>Thumbnail</th>
                  <th style={{ padding: '14px 16px' }}>Video Details</th>
                  <th style={{ padding: '14px 16px', width: '120px' }}>Source</th>
                  <th style={{ padding: '14px 16px', width: '120px' }}>Platform</th>
                  <th style={{ padding: '14px 16px', width: '140px' }}>Placement</th>
                  <th style={{ padding: '14px 16px', width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '14px 20px', width: '140px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPromotions.map((promo, idx) => (
                  <tr
                    key={promo.id}
                    style={{
                      borderBottom: '1px solid var(--border-primary)',
                      transition: 'background-color 0.15s ease'
                    }}
                  >
                    {/* Priority / Order Reorder Buttons */}
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => handleMoveOrder(idx, 'up')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: idx === 0 ? 'var(--border-primary)' : 'var(--text-secondary)',
                            cursor: idx === 0 ? 'default' : 'pointer',
                            padding: '2px'
                          }}
                          title="Move Up"
                        >
                          <MoveUp size={14} />
                        </button>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
                          #{promo.priority || idx + 1}
                        </span>
                        <button
                          type="button"
                          disabled={idx === filteredPromotions.length - 1}
                          onClick={() => handleMoveOrder(idx, 'down')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: idx === filteredPromotions.length - 1 ? 'var(--border-primary)' : 'var(--text-secondary)',
                            cursor: idx === filteredPromotions.length - 1 ? 'default' : 'pointer',
                            padding: '2px'
                          }}
                          title="Move Down"
                        >
                          <MoveDown size={14} />
                        </button>
                      </div>
                    </td>

                    {/* Thumbnail Preview with Play Overlay */}
                    <td style={{ padding: '16px' }}>
                      <div
                        onClick={() => setPreviewVideo(promo)}
                        style={{
                          position: 'relative',
                          width: '110px',
                          height: '66px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#000000',
                          cursor: 'pointer',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                        }}
                      >
                        <img
                          src={promo.thumbnail_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=400&q=80'}
                          alt={promo.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background-color 0.2s ease'
                        }}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--primary)'
                          }}>
                            <Play size={14} style={{ marginLeft: '2px' }} />
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Video Details */}
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                        {promo.title}
                      </div>
                      {promo.description && (
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--text-secondary)',
                          lineHeight: '1.4',
                          maxWidth: '420px',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {promo.description}
                        </div>
                      )}
                      {(promo.start_date || promo.end_date) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--primary)', marginTop: '4px' }}>
                          <Calendar size={12} />
                          <span>
                            {promo.start_date ? new Date(promo.start_date).toLocaleDateString() : 'Now'}
                            {' → '}
                            {promo.end_date ? new Date(promo.end_date).toLocaleDateString() : 'Forever'}
                          </span>
                        </div>
                      )}
                    </td>

                    {/* Source Badge */}
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: promo.source_type === 'youtube'
                          ? 'rgba(239, 68, 68, 0.1)'
                          : 'rgba(16, 185, 129, 0.1)',
                        color: promo.source_type === 'youtube' ? '#dc2626' : '#059669'
                      }}>
                        {promo.source_type === 'youtube' ? <LinkIcon size={12} /> : <Upload size={12} />}
                        {promo.source_type === 'youtube' ? 'YouTube' : 'MP4 File'}
                      </span>
                    </td>

                    {/* Platform Badge */}
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: 'var(--text-primary)'
                      }}>
                        {promo.show_on === 'Both' && <Layers size={14} style={{ color: 'var(--primary)' }} />}
                        {promo.show_on === 'Website' && <Globe size={14} style={{ color: '#2563eb' }} />}
                        {promo.show_on === 'App' && <Smartphone size={14} style={{ color: '#059669' }} />}
                        {promo.show_on}
                      </span>
                    </td>

                    {/* Position Placement Badge */}
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: 'var(--text-secondary)',
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        padding: '3px 8px',
                        borderRadius: '6px'
                      }}>
                        {promo.position}
                      </span>
                    </td>

                    {/* Status Toggle */}
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(promo)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: promo.status === 'Active' ? '#059669' : '#94a3b8'
                        }}
                        title="Click to toggle status"
                      >
                        {promo.status === 'Active' ? (
                          <>
                            <CheckCircle size={16} />
                            <span>Active</span>
                          </>
                        ) : (
                          <>
                            <XCircle size={16} />
                            <span>Inactive</span>
                          </>
                        )}
                      </button>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => setPreviewVideo(promo)}
                          className="btn-icon"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Preview Video"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenModal(promo)}
                          className="btn-icon"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Edit Video"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(promo)}
                          className="btn-icon"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid rgba(220, 38, 38, 0.2)',
                            backgroundColor: 'rgba(220, 38, 38, 0.05)',
                            color: '#dc2626',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Delete Video"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ADD / EDIT PROMOTION VIDEO MODAL                                          */}
      {/* ========================================================================= */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#FFFEFE',
            color: '#0f172a',
            borderRadius: '18px',
            maxWidth: '680px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
            border: '1px solid var(--border-primary)',
            position: 'relative'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#FFFEFE'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                  {editingPromo ? 'Edit Promotion Video' : 'Add Brand Promotion Video'}
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Provide video source, thumbnail, and display preferences.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              {formError && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  border: '1px solid rgba(220, 38, 38, 0.3)',
                  color: '#b91c1c',
                  fontSize: '13px',
                  marginBottom: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={16} />
                  <span>{formError}</span>
                </div>
              )}

              {/* Title Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                  Video Title <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Zero Brokerage Advantage in Ghazipur"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{
                    width: '100%',
                    height: '44px',
                    padding: '0 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    color: '#0f172a',
                    backgroundColor: '#ffffff'
                  }}
                />
              </div>

              {/* Description Input */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                  Description (Optional)
                </label>
                <textarea
                  rows={2}
                  placeholder="Brief summary of what this video highlights..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    color: '#0f172a',
                    backgroundColor: '#ffffff',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Source Type Selector */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                  Video Source <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setSourceType('youtube')}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: sourceType === 'youtube' ? '2px solid var(--primary)' : '1px solid #cbd5e1',
                      backgroundColor: sourceType === 'youtube' ? 'rgba(168, 8, 12, 0.04)' : '#ffffff',
                      color: sourceType === 'youtube' ? 'var(--primary)' : '#475569',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontWeight: 600,
                      fontSize: '13px',
                      textAlign: 'left'
                    }}
                  >
                    <LinkIcon size={18} />
                    <div>
                      <div>YouTube / Vimeo Link</div>
                      <div style={{ fontSize: '11px', fontWeight: 400, opacity: 0.8 }}>Auto fetches thumbnail</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSourceType('upload')}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: sourceType === 'upload' ? '2px solid var(--primary)' : '1px solid #cbd5e1',
                      backgroundColor: sourceType === 'upload' ? 'rgba(168, 8, 12, 0.04)' : '#ffffff',
                      color: sourceType === 'upload' ? 'var(--primary)' : '#475569',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontWeight: 600,
                      fontSize: '13px',
                      textAlign: 'left'
                    }}
                  >
                    <Upload size={18} />
                    <div>
                      <div>MP4 / WebM Upload</div>
                      <div style={{ fontSize: '11px', fontWeight: 400, opacity: 0.8 }}>Upload to Cloud Storage</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Source Option A: YouTube/Vimeo Link */}
              {sourceType === 'youtube' ? (
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Paste Video URL (YouTube or Vimeo) <span style={{ color: 'var(--primary)' }}>*</span>
                  </label>
                  <input
                    type="url"
                    placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                    value={formData.video_url}
                    onChange={(e) => handleVideoUrlChange(e.target.value)}
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '0 14px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                    Thumbnail will be auto-generated from your YouTube/Vimeo link.
                  </div>
                </div>
              ) : (
                /* Source Option B: MP4/WebM Upload */
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Choose Video File (MP4, WebM - Max 60MB) <span style={{ color: 'var(--primary)' }}>*</span>
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="video/mp4,video/webm"
                    onChange={handleVideoFileSelect}
                    style={{ display: 'none' }}
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '2px dashed #cbd5e1',
                      borderRadius: '12px',
                      padding: '24px 16px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: '#f8fafc',
                      transition: 'border-color 0.2s ease'
                    }}
                  >
                    <Upload size={24} style={{ color: 'var(--primary)', margin: '0 auto 8px' }} />
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                      {videoFile ? videoFile.name : (formData.video_url ? 'Existing file uploaded (click to replace)' : 'Click to select MP4/WebM video')}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                      {videoFile ? `${(videoFile.size / (1024 * 1024)).toFixed(1)} MB` : 'Supported: MP4, WebM (up to 60MB)'}
                    </div>
                  </div>

                  {/* Upload Progress Bar */}
                  {uploadProgress > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '4px' }}>
                        <span>Uploading Video to Cloud...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${uploadProgress}%`,
                          height: '100%',
                          backgroundColor: 'var(--primary)',
                          transition: 'width 0.2s ease'
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Thumbnail Section */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                  Thumbnail Image (Auto-detected or Custom Upload)
                </label>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                  {formData.thumbnail_url ? (
                    <div style={{
                      width: '100px',
                      height: '60px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #cbd5e1',
                      backgroundColor: '#000000',
                      flexShrink: 0
                    }}>
                      <img src={formData.thumbnail_url} alt="Thumbnail preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ) : null}
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      ref={thumbInputRef}
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleThumbnailFileSelect}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => thumbInputRef.current?.click()}
                      className="btn"
                      style={{
                        height: '38px',
                        padding: '0 14px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#475569',
                        cursor: 'pointer'
                      }}
                    >
                      <Upload size={14} /> Upload Custom Thumbnail
                    </button>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                      Replaces auto-thumbnail if provided. JPG, PNG or WebP.
                    </div>
                  </div>
                </div>
              </div>

              {/* 3-Column Settings: Show On, Position, Priority */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '18px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Show On Platform
                  </label>
                  <select
                    value={formData.show_on}
                    onChange={(e) => setFormData({ ...formData, show_on: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  >
                    <option value="Both">Both (Website & App)</option>
                    <option value="Website">Website Only</option>
                    <option value="App">Mobile App Only</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Display Placement
                  </label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  >
                    <option value="Promotion Section">Promotion Section</option>
                    <option value="Homepage Hero">Homepage Hero</option>
                    <option value="Reels Page">Reels Page</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Display Priority (Order)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  />
                </div>
              </div>

              {/* Status and Scheduling */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Publish Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  >
                    <option value="Active">Active (Live)</option>
                    <option value="Inactive">Inactive (Draft)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    Start Date (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>
                    End Date (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    style={{
                      width: '100%',
                      height: '42px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff'
                    }}
                  />
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '18px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                  style={{
                    height: '44px',
                    padding: '0 18px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                  style={{
                    height: '44px',
                    padding: '0 24px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      <span>Saving Video...</span>
                    </>
                  ) : (
                    <span>{editingPromo ? 'Update Video' : 'Publish Promotion Video'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* IN-ADMIN VIDEO LIVE PREVIEW MODAL                                         */}
      {/* ========================================================================= */}
      {previewVideo && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#0f172a',
            borderRadius: '18px',
            maxWidth: '820px',
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative'
          }}>
            {/* Preview Header */}
            <div style={{
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Film size={18} style={{ color: 'var(--primary)' }} />
                <span style={{ fontSize: '15px', fontWeight: 600, color: '#ffffff' }}>
                  {previewVideo.title}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewVideo(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Video Player */}
            <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: '#000000' }}>
              {previewVideo.source_type === 'youtube' ? (
                (() => {
                  const ytId = extractYouTubeId(previewVideo.video_url);
                  return ytId ? (
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0`}
                      title={previewVideo.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                    />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                      Invalid YouTube link
                    </div>
                  );
                })()
              ) : previewVideo.video_url?.includes('vimeo.com') ? (
                (() => {
                  const match = previewVideo.video_url.match(/vimeo\.com\/(\d+)/);
                  const vimeoId = match ? match[1] : null;
                  return vimeoId ? (
                    <iframe
                      src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`}
                      title={previewVideo.title}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                    />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                      Invalid Vimeo link
                    </div>
                  );
                })()
              ) : (
                <video
                  controls
                  autoPlay
                  src={previewVideo.video_url}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>

            {/* Preview Footer Details */}
            <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#94a3b8', fontSize: '13px' }}>
              <div>
                Platform: <strong style={{ color: '#ffffff' }}>{previewVideo.show_on}</strong> • Placement: <strong style={{ color: '#ffffff' }}>{previewVideo.position}</strong>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const target = previewVideo;
                    setPreviewVideo(null);
                    handleOpenModal(target);
                  }}
                  className="btn"
                  style={{
                    height: '34px',
                    padding: '0 14px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <Edit2 size={13} /> Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        type="danger"
        confirmText="Delete Video"
        onConfirm={async () => {
          if (pendingAction) await pendingAction();
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default PromotionVideos;
